import type { ReactNode } from 'react'
import type { LabeledValue, DefaultOptionType } from 'antd/es/select'
import type { EasyRemoteProps, EasyRemoteSearch, EasyRemoteDropDown } from './declare'
import { useState, useEffect, useMemo, useRef } from 'react'
import { Pagination, Select, Divider, Spin } from 'antd'
import EasyImg from './EasyImg'
import { matchFile } from 'src/utils/tools'
import { get, debounce, merge, isEmpty, isEqual } from 'lodash'
import 'src/styles/components/common/easyFileSelect.less'

// 下拉框的额外渲染
const DropDownRender = (props: EasyRemoteDropDown) => {
  const [pagination, setPagination] = useState({ total: 0, current: 1 })
  useEffect(() => {
    setPagination(prev => isEqual(prev, props.pagination) ? prev : props.pagination)
  }, [props.pagination])

  const getExtraAddon = () => {
    const { addonDropdown } = props
    if (!addonDropdown?.show) return null
    if (addonDropdown.render instanceof Function) return addonDropdown.render()
    const className = `file-select__dropdown ${addonDropdown.selected ? 'selected' : ''}`
    return (
      <div onClick={addonDropdown.onSelect} className={className}>
        <span>自定义</span>
      </div>
    )
  }

  const handleChange = (current: number) => {
    setPagination(prev => ({ ...prev, current }))
    props.onChange(current)
  }

  const { defaultMenu, paginationProps } = props
  return (
    <Spin spinning={props.loading}>
      {getExtraAddon()}
      {defaultMenu}
      <Divider style={{ margin: '4px 0' }} />
      <Pagination
        simple
        size='small'
        total={pagination.total}
        style={{ textAlign: 'center' }}
        {...paginationProps}
        current={pagination.current}
        onChange={handleChange}
      />
    </Spin>
  )
}

// Select 后的额外渲染
const AddonAfterRender = (props: {
  value: EasyRemoteProps['value'],
  addonAfter: ReactNode,
}) => {
  return (
    <div className='file-select__addon'>
      {props.addonAfter}
    </div>
  )
}

const EasyFileSelect = (props: EasyRemoteProps & { fileDomain?: string }) => {
  const fieldOption = useMemo<EasyRemoteProps['fieldOption']>(() => {
    return merge({
      data: 'Data',
      total: 'TotalCount',
      value: 'ID',
      labels: ['NAME'],
      labelGapString: ['（', '）']
    }, props.fieldOption)
  }, [props.fieldOption])

  const [search, setSearch] = useState<EasyRemoteSearch>({
    value: '', // 搜索值
    current: 1, // 分页数据
  })

  const handleSearch = debounce((value) => {
    setSearch({ value, current: 1 })
  }, props.debounceTime ?? 500)

  const handleChange = (
    value: LabeledValue | LabeledValue[] | undefined,
    option: DefaultOptionType | DefaultOptionType[] | undefined
  ) => {
    /**
     * 单选时，清除选中，value 为 undefined
     * 多选时，清除选中，value 为 []
     */
    props.onChange?.(value, option)
    // 清除选中，重新发起搜索
    isEmpty(value) && setSearch({ value: '', current: 1 })
  }

  /**
   * 搜索结果数据
   */
  const [data, setData] = useState({ data: [], total: 0 })
  const [dataLoading, setDataLoading] = useState(false)

  const [focused, setFocused] = useState(!!props.forceRender)

  const promiseQueue = useRef<any[]>([])
  useEffect(() => {
    // 只在 focus 之后请求分页树数据
    if (!focused) return

    // 此处逻辑是为了保证调用顺序和接口返回数据一致，避免调用两次，而第二次的数据返回快于第一次的情况
    promiseQueue.current.push({
      doing: false,
      func: () => {
        if (props.remoteMethod instanceof Function) {
          return props.remoteMethod({
            ...search,
            remotePayload: props.remotePayload
          }).then((res: any) => {
            setData(prev => {
              const newData = {
                data: get(res, fieldOption!.data!) ?? [],
                total: get(res, fieldOption!.total!) ?? 0
              }
              return isEqual(prev, newData) ? prev : newData
            })
          })
        } else {
          return Promise.resolve()
        }
      }
    })

    function loop (arr: any) {
      if (!arr[0].doing) {
        arr[0].doing = true
        setDataLoading(true)
        arr[0].func().finally(() => {
          setDataLoading(false)
          arr.splice(0, 1)
          arr.length && loop(arr)
        })
      }
    }

    loop(promiseQueue.current)
  }, [focused, search])

  const pagination = useMemo(() => {
    return {
      total: data.total,
      current: search.current
    }
  }, [data.total, search.current])

  const selectedValue = useMemo(() => {
    const getLabel = (label: ReactNode) => {
      if (!matchFile(String(label))) return label
      let target = label as string
      !target.startsWith('http') && (target = `${props.fileDomain}${target}`)
      return <div className='file-select__label'><EasyImg src={target} /></div>
    }
    if (Array.isArray(props.value)) {
      return props.value.map(item => {
        const { label, value } = item
        return { label: getLabel(label), value }
      })
    }
    const { label, value } = (props.value ?? {}) as LabeledValue
    return { label: getLabel(label), value }
  }, [props.value])

  const { selectProps } = props
  return (
    <div className={`file-select ${props.className}`} style={props.style}>
      <Select
        allowClear
        showSearch
        labelInValue
        loading={dataLoading}
        placeholder='请选择'
        filterOption={false}
        defaultActiveFirstOption={false}
        {...selectProps}
        virtual={false}
        optionLabelProp={fieldOption!.inputLabels?.length ? 'input' : 'title'}
        mode={props.multipleMode ? 'multiple' : undefined}
        value={selectedValue}
        onFocus={() => setFocused(true)}
        onSearch={handleSearch}
        onChange={handleChange}
        dropdownRender={menu => (
          <DropDownRender
            loading={selectProps?.loading || dataLoading}
            defaultMenu={menu}
            addonDropdown={props.addonDropdown}
            pagination={pagination}
            paginationProps={props.paginationProps}
            onChange={current => setSearch(prev => ({ ...prev, current }))}
          />
        )}
        popupClassName='file-select__popup'
        className={`file-select__select ${selectProps?.className}`}
      >
        {
          data.data.map(item => {
            const { value: keyField, labels, inputLabels, labelGapString } = fieldOption!
            const [gapF, gapE] = labelGapString!
            const getLabel = (fields?: string[]) => {
              return fields?.map((field, index) => {
                let target: any = get(item, field)
                if (matchFile(target)) {
                  !target.startsWith('http') && (target = `${props.fileDomain}${target}`)
                  target = <EasyImg key={`${field}_${index}`} src={target} />
                  return index === 0 ? target : <span key={`${field}_${index}`}>{gapF}{target}{gapE}</span>
                }
                return index === 0 ? target : `${gapF}${target}${gapE}`
              })
            }
            const label = getLabel(labels)
            const inputLabel = getLabel(inputLabels)
            const title = label?.filter(la => typeof la === 'string').join('')
            const value = get(item, keyField!)
            return (
              <Select.Option
                key={value}
                value={value}
                title={title}
                input={
                  <div className='file-select__label'>
                    {inputLabel?.map((la, li) => typeof la === 'string' ? <span key={`${la}_${li}`}>{la}</span> : la)}
                  </div>
                }
              >
                <div className='file-select__option'>
                  <div className='file-select__label'>
                    {label?.map((la, li) => typeof la === 'string' ? <span key={`${la}_${li}`}>{la}</span> : la)}
                  </div>
                </div>
              </Select.Option>
            )
          })
        }
      </Select>
      <AddonAfterRender value={props.value} addonAfter={props.addonAfter} />
    </div>
  )
}

export default EasyFileSelect
